import { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useApi } from "../../hooks/useApi";
import { EthocaMisroutedDescriptor } from "../../types/ethocaMisroutedDescriptor";
import MisroutedDescriptorDialog from "./MisroutedDescriptorDialog";
import ConfirmDialog from "../common/ConfirmDialog";
import { formatMisroutedDescriptorSaveMessage } from "../../utils/formatMisroutedDescriptorSaveResult";

dayjs.extend(utc);

const formatCreatedAt = (iso: string) => dayjs.utc(iso).format("MMM DD, YYYY HH:mm");

interface MisroutedDescriptorSettingsProps {
  companyId: number | null;
}

export default function MisroutedDescriptorSettings({ companyId }: MisroutedDescriptorSettingsProps) {
  const { get, loading } = useApi();
  const { del, loading: deleting, error: deleteError } = useApi();
  const [rows, setRows] = useState<EthocaMisroutedDescriptor[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<EthocaMisroutedDescriptor | null>(null);
  const [deletingRow, setDeletingRow] = useState<EthocaMisroutedDescriptor | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

  const fetchRows = useCallback(async () => {
    if (!companyId) return;
    setFetchError(false);
    try {
      const response = await get<{ misroutedDescriptors: EthocaMisroutedDescriptor[] }>(
        `/api/alerts/ethoca/misrouted-descriptors?companyId=${companyId}`
      );
      setRows(response.misroutedDescriptors);
    } catch (err) {
      console.error("Failed to fetch misrouted descriptors:", err);
      setFetchError(true);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleOpenCreate = () => {
    setEditingRow(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row: EthocaMisroutedDescriptor) => {
    setEditingRow(row);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRow(null);
  };

  const handleSaved = (billedAlertsCount?: number) => {
    handleCloseDialog();
    fetchRows();
    setSnackbar({ open: true, message: formatMisroutedDescriptorSaveMessage(billedAlertsCount) });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDelete = async () => {
    if (!deletingRow || deleting) return;
    try {
      await del(`/api/alerts/ethoca/misrouted-descriptors/${deletingRow.ethoca_misrouted_descriptor_id}`);
      setDeletingRow(null);
      fetchRows();
    } catch (err) {
      console.error("Failed to delete misrouted descriptor:", err);
    }
  };

  if (!companyId) {
    return (
      <Paper
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
          px: 4,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Please select a company from the header to manage misrouted descriptors.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Misrouted Descriptors
      </Typography>

      {deleteError && (
        <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          Error: {deleteError}
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Descriptor prefixes Ethoca misroutes to this company. Matching alerts bill to the named profile instead.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            backgroundColor: "#465fff",
            "&:hover": { backgroundColor: "#3641f5" },
          }}
        >
          Add
        </Button>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Descriptor prefix</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Billing profile</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Created</TableCell>
                <TableCell sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetchError ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 2 }}>
                    <Alert severity="error">Failed to load misrouted descriptors.</Alert>
                  </TableCell>
                </TableRow>
              ) : loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No misrouted descriptors found. Click &quot;Add&quot; to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.ethoca_misrouted_descriptor_id} hover>
                    <TableCell>{row.descriptor_prefix}</TableCell>
                    <TableCell>{row.company_name || `#${row.company_id}`}</TableCell>
                    <TableCell>{row.billing_profile_name}</TableCell>
                    <TableCell>{row.created_by_name || "--"}</TableCell>
                    <TableCell>{formatCreatedAt(row.created_at)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeletingRow(row)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {dialogOpen && (
        <MisroutedDescriptorDialog
          open={dialogOpen}
          companyId={companyId}
          initialDescriptor=""
          misroutedDescriptor={editingRow ?? undefined}
          onClose={handleCloseDialog}
          onSaved={handleSaved}
        />
      )}

      {deletingRow && (
        <ConfirmDialog
          open={!!deletingRow}
          title="Delete misrouted descriptor"
          body={`Are you sure you want to delete the misrouted descriptor "${deletingRow.descriptor_prefix}"?`}
          confirmLabel="Delete"
          onClose={() => setDeletingRow(null)}
          onConfirm={handleDelete}
        />
      )}

      <Snackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
